import type { Pagination } from '../types/common/Pagination';
import type { OrderByDescriptor } from '../types/common/OrderByDescriptor';
import type { BoundaryFilter } from '../types/common/BoundaryFilter';
import type { ListingQueryModel } from '../types/listings/ListingQueryModel';
import type { SpecFilter } from '../types/listings/SpecFilter';

/**
 * Build flat query params for pagination.
 * Backend expects Pagination.PageSize, Pagination.PageNumber.
 */
export function buildPaginationParams(pagination?: Pagination | null): Record<string, number> {
  if (!pagination) return {};
  const out: Record<string, number> = {};
  if (pagination.pageSize != null) out['Pagination.PageSize'] = pagination.pageSize;
  if (pagination.pageNumber != null) out['Pagination.PageNumber'] = pagination.pageNumber;
  return out;
}

/**
 * Build flat query params for multi-field ordering.
 * Backend expects OrderBy[0].Field, OrderBy[0].Descending, OrderBy[1].Field, ...
 */
export function buildOrderByParams(orderBy?: OrderByDescriptor[] | null): Record<string, string | boolean> {
  if (!orderBy?.length) return {};
  const out: Record<string, string | boolean> = {};
  orderBy.forEach((o, i) => {
    if (o.field != null) out[`OrderBy[${i}].Field`] = o.field;
    out[`OrderBy[${i}].Descending`] = o.descending ?? false;
  });
  return out;
}

/**
 * Build flat query params for a numeric boundary filter (e.g. Price).
 * Backend expects Price.LowerBound, Price.UpperBound, Price.ExactMatch.
 */
export function buildBoundaryParams<T extends number>(
  key: string,
  filter?: BoundaryFilter<T> | null
): Record<string, number> {
  if (!filter) return {};
  const out: Record<string, number> = {};
  if (filter.lowerBound != null) out[`${key}.LowerBound`] = filter.lowerBound;
  if (filter.upperBound != null) out[`${key}.UpperBound`] = filter.upperBound;
  if (filter.exactMatch != null) out[`${key}.ExactMatch`] = filter.exactMatch;
  return out;
}

/**
 * Build flat query params for Specs (array of SpecFilter).
 * Swagger: parameter name "Specs" with items: SpecFilter (key, value, valueType, operator).
 * ASP.NET-style serialization: Specs[0].key, Specs[0].value, Specs[0].valueType, Specs[0].operator, ...
 * Empty/null keys or values are omitted.
 */
function buildSpecsParams(specs?: SpecFilter[] | null): Record<string, string | number> {
  if (!specs?.length) return {};
  const out: Record<string, string | number> = {};
  specs.forEach((s, i) => {
    if (s.key != null && s.key !== '') {
      out[`Specs[${i}].key`] = s.key;
    }
    if (s.value != null && s.value !== '') {
      out[`Specs[${i}].value`] = s.value;
    }
    // valueType is required in Swagger SpecFilter
    out[`Specs[${i}].valueType`] = s.valueType;
    if (s.operator != null && s.operator !== '') {
      out[`Specs[${i}].operator`] = s.operator;
    }
  });
  return out;
}

/**
 * Build flat query params for GET /api/listing from ListingQueryModel.
 * Pagination, OrderBy, Price boundary, scalar filters, and Specs.
 */
export function buildListingQueryParams(q: ListingQueryModel | undefined): Record<string, unknown> {
  if (!q) return {};
  const out: Record<string, unknown> = {};
  if (q.UserId != null) out.UserId = q.UserId;
  if (q.TopCategoryId != null) out.TopCategoryId = q.TopCategoryId;
  if (q.SecondaryCategoryId != null) out.SecondaryCategoryId = q.SecondaryCategoryId;
  if (q.ProductTypeId != null) out.ProductTypeId = q.ProductTypeId;
  if (q.ConditionId != null) out.ConditionId = q.ConditionId;
  if (q.StatusId != null) out.StatusId = q.StatusId;
  if (q.CityId != null) out.CityId = q.CityId;
  if (q.DistrictId != null) out.DistrictId = q.DistrictId;
  if (q.IsNew != null) out.IsNew = q.IsNew;
  Object.assign(out, buildPaginationParams(q.Pagination ?? undefined));
  Object.assign(out, buildOrderByParams(q.OrderBy ?? undefined));
  Object.assign(out, buildBoundaryParams('Price', q.Price ?? undefined));
  Object.assign(out, buildSpecsParams(q.Specs ?? undefined));
  return out;
}
